import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';
import services from '../../services';

import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';

import AddIcon from '@material-ui/icons/Add';

import SimpleTable from '../../components/super/SimpleTable';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import ErrorMessage from '../../components/errormessage';

function ContentGroupsList() {
    const { superStore, commonStore } = useStores();
    const navigate = useNavigate();
    const { aclGroups } = superStore;

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupDescription, setNewGroupDescription] = useState('');
    const [savingGroup, setSavingGroup] = useState(false);

    useEffect(() => {
        superStore.loadAclGroups();
    }, [superStore]);

    const handleOpenCreateModal = () => {
        setNewGroupName('');
        setNewGroupDescription('');
        setCreateModalOpen(true);
    };

    const handleCloseCreateModal = () => {
        if (!savingGroup) {
            setCreateModalOpen(false);
        }
    };

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) {
            commonStore.error('Group name is required');
            return;
        }

        setSavingGroup(true);
        try {
            const result = await services.ACL.createGroup({
                name: newGroupName.trim(),
                description: newGroupDescription.trim() || undefined,
            });
            await superStore.loadAclGroups();
            setCreateModalOpen(false);
            setNewGroupName('');
            setNewGroupDescription('');
            const groupId = result?.unique_id ?? result?.id;
            if (groupId) {
                navigate(String(groupId), { relative: 'path' });
            }
        } catch (e) {
            commonStore.error('Failed to create content group');
        } finally {
            setSavingGroup(false);
        }
    };

    const columns = React.useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: (x) => (
                    <Link to={String(x.row.original.unique_id)}>
                        {x.value}
                    </Link>
                ),
            },
            {
                Header: 'Id',
                accessor: 'unique_id',
                Cell: (x) => (
                    <Link to={String(x.value)}>
                        {x.value}
                    </Link>
                ),
            },
        ],
        []
    );

    return (
        <Grid
            container
            direction="row"
            justify="center"
            align="center"
            spacing={2}
        >
            <Grid item xs={12}>
                <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h2" gutterBottom>
                        Content Groups
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateModal}
                    >
                        Create group
                    </Button>
                </Box>
            </Grid>

            <Grid item xs={12}>
                <ErrorMessage error={superStore.aclError} />
            </Grid>

            <Grid item xs={12}>
                {superStore.aclGroups.length === 0 && !aclGroups ? (
                    <ThemedSpinner />
                ) : aclGroups.length === 0 ? (
                    <Typography color="textSecondary">No content groups yet. Create one to get started.</Typography>
                ) : (
                    <SimpleTable
                        columns={columns}
                        data={aclGroups.slice()}
                        needsFilter={aclGroups.length > 10}
                    />
                )}
            </Grid>

            <Dialog open={createModalOpen} onClose={handleCloseCreateModal} maxWidth="sm" fullWidth>
                <DialogTitle>Create content group</DialogTitle>
                <DialogContent>
                    <Box pt={1}>
                        <TextField
                            autoFocus
                            label="Name"
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            fullWidth
                            required
                            variant="outlined"
                            margin="normal"
                        />
                        <TextField
                            label="Description (optional)"
                            value={newGroupDescription}
                            onChange={(e) => setNewGroupDescription(e.target.value)}
                            fullWidth
                            variant="outlined"
                            margin="normal"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreateModal} disabled={savingGroup}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateGroup}
                        color="primary"
                        variant="contained"
                        disabled={savingGroup || !newGroupName.trim()}
                    >
                        {savingGroup ? <CircularProgress size={24} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

export default observer(ContentGroupsList);
